import axios from 'axios';
import { API_BASE_URL, SOCKET_URL } from '../config';
import { isBuiltinAvatarSpec, resolveBuiltinAvatar } from './builtinAvatars';

class APIError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
    this.name = 'APIError';
  }
}

// Extract tenant info from URL
export const getTenantInfoFromURL = () => {
  const path = window.location.pathname;
  
  // Match patterns like /chat/tenantId/scriptHash or /script/tenantId/scriptHash
  const chatMatch = path.match(/^\/chat\/([a-z0-9]{6,12})\/([a-f0-9]{16})$/);
  const scriptMatch = path.match(/^\/script\/([a-z0-9]{6,12})\/([a-f0-9]{16})$/);
  
  if (chatMatch) {
    return {
      tenantId: chatMatch[1],
      scriptHash: chatMatch[2],
      type: 'chat'
    };
  }
  
  if (scriptMatch) {
    return {
      tenantId: scriptMatch[1],
      scriptHash: scriptMatch[2], 
      type: 'script'
    };
  }
  
  // For development, use default tenant if no match
  if (process.env.NODE_ENV === 'development') {
    return {
      tenantId: 'eolibo11',
      scriptHash: '7f4d4e7d30501406',
      type: 'development'
    };
  }
  
  return null;
};


// Storage utilities for auth data
export const authStorage = {
  save: (authData) => {
    localStorage.setItem('chatAuth', JSON.stringify({
      ...authData,
      timestamp: Date.now(),
      consumed: false
    }));
  },

  load: () => {
    try {
      const data = localStorage.getItem('chatAuth');
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading auth data:', error);
      return null;
    }
  },

  // Mark auth as consumed (used by a tab) and clear it
  consume: () => {
    try {
      const data = localStorage.getItem('chatAuth');
      if (data) {
        const authData = JSON.parse(data);
        if (authData.consumed) {
          // Already consumed by another tab
          return null;
        }
        // Mark as consumed and clear from storage
        localStorage.removeItem('chatAuth');
        return authData;
      }
      return null;
    } catch (error) {
      console.error('Error consuming auth data:', error);
      return null;
    }
  },

  clear: () => {
    localStorage.removeItem('chatAuth');
  },

  isExpired: (authData, maxAge = 7 * 24 * 60 * 60 * 1000) => { // 7 days default
    if (!authData || !authData.timestamp) {
      return true;
    }
    return Date.now() - authData.timestamp > maxAge;
  }
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const authData = authStorage.load();
  console.log('Axios interceptor - authData:', authData);
  if (authData?.token) {
    config.headers.Authorization = `Bearer ${authData.token}`;
    console.log('Added Authorization header:', config.headers.Authorization);
  } else {
    console.log('No token found in authData');
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      authStorage.clear();
      // Don't redirect in tenant context - let the app handle it
    }
    return Promise.reject(error);
  }
);

// Tenant-scoped Auth API
export const authAPI = {
  // Register a new user
  register: async (tenantId, scriptHash, userData) => {
    try {
      const response = await api.post(`/tenant/${tenantId}/${scriptHash}/auth/register`, {
        username: userData.username,
        email: userData.email || undefined,
        password: userData.password,
        confirmPassword: userData.confirmPassword,
        agreeToTerms: userData.agreeToTerms
      });
      
      return response.data.data; // Extract the nested data property
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Registration failed',
        error.response?.status || 500
      );
    }
  },

  // Login an existing user
  login: async (tenantId, scriptHash, credentials) => {
    try {
      const response = await api.post(`/tenant/${tenantId}/${scriptHash}/auth/login`, {
        username: credentials.username || credentials.email, // Support both username and email
        password: credentials.password
      });
      
      return response.data.data; // Extract the nested data property
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Login failed',
        error.response?.status || 500
      );
    }
  },

  // Create a guest user
  guest: async (tenantId, scriptHash) => {
    try {
      const response = await api.post(`/tenant/${tenantId}/${scriptHash}/auth/guest`);
      return response.data.data; // Extract the nested data property
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Guest login failed',
        error.response?.status || 500
      );
    }
  },

  // Get tenant configuration
  getConfig: async (tenantId, scriptHash) => {
    try {
      const response = await api.get(`/tenant/${tenantId}/${scriptHash}/config`);
      return response.data.data; // Extract the nested data property
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Failed to load configuration',
        error.response?.status || 500
      );
    }
  }
};

// Rooms API (tenant-scoped)
export const roomsAPI = {
  getRooms: (tenantId, scriptHash) =>
    api.get(`/tenant/${tenantId}/${scriptHash}/rooms`),
  
  joinRoom: (tenantId, scriptHash, roomId) =>
    api.post(`/tenant/${tenantId}/${scriptHash}/rooms/${roomId}/join`)
};

// Messages API (tenant-scoped)
export const messagesAPI = {
  // Get recent messages for a room
  getMessages: async (tenantId, scriptHash, roomName = null) => {
    try {
      const params = roomName ? `?roomName=${encodeURIComponent(roomName)}` : '';
      const response = await api.get(`/tenant/${tenantId}/${scriptHash}/messages${params}`);
      return response.data;
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Failed to load messages',
        error.response?.status || 500
      );
    }
  },

  // Send a message to a room
  sendMessage: async (tenantId, scriptHash, messageData) => {
    try {
      const response = await api.post(`/tenant/${tenantId}/${scriptHash}/messages`, {
        content: messageData.content,
        roomName: messageData.roomName || null,
        type: messageData.type || 'TEXT'
      });
      return response.data;
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Failed to send message',
        error.response?.status || 500
      );
    }
  },

  // Get users in a room
  getRoomUsers: async (tenantId, scriptHash, roomName = null) => {
    try {
      const roomParam = roomName || 'general';
      const response = await api.get(`/tenant/${tenantId}/${scriptHash}/rooms/${encodeURIComponent(roomParam)}/users`);
      return response.data;
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Failed to load room users',
        error.response?.status || 500
      );
    }
  }
};

// Resolve a stored image URL (relative "/uploads/..." path returned by the
// upload API) into an absolute URL against the backend origin, so an <img src>
// loads from the backend (port 3001) rather than the chat dev server.
export const resolveUploadUrl = (url) => {
  if (!url) return url;
  if (/^https?:\/\//i.test(url)) return url; // already absolute
  return `${SOCKET_URL}${url}`;
};

// Resolve an avatar URL for an <img src>. Uploaded avatars are backend-hosted
// ("/uploads/..."), so they must resolve against the backend origin. Built-in
// tintable avatars are stored as "builtin:<NN>:<hex>" specs and render locally
// to an SVG data URL (see builtinAvatars.js). Absolute URLs (legacy/external)
// pass through untouched. No avatar at all — or the "/default-avatar.svg"
// placeholder the server emits for users who never set one — resolves to
// undefined, so the MUI Avatar falls through to its children instead:
// <AvatarFallback>, the theme-tinted builtin default avatar.
export const resolveAvatarUrl = (url) => {
  if (!url || url.endsWith('/default-avatar.svg')) return undefined;
  if (url.startsWith('/uploads/')) return `${SOCKET_URL}${url}`;
  if (isBuiltinAvatarSpec(url)) return resolveBuiltinAvatar(url) || url;
  return url; // an already-absolute URL
};

// Upload API (tenant-scoped) — images for inline chat sending.
export const uploadAPI = {
  // Upload a single image file. Returns the stored relative URL (e.g.
  // "/uploads/<instanceId>/<name>.png"). Reuses the shared axios instance so
  // the Bearer token is attached by the request interceptor.
  uploadImage: async (tenantId, scriptHash, file) => {
    try {
      const form = new FormData();
      form.append('image', file);
      const response = await api.post(
        `/tenant/${tenantId}/${scriptHash}/upload/image`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          // Images can be a few MB on slow links — give it more than the
          // default 10s request timeout.
          timeout: 30000,
        }
      );
      return response.data.data; // { url, type, size }
    } catch (error) {
      throw new APIError(
        error.response?.data?.error || 'Image upload failed',
        error.response?.status || 500
      );
    }
  }
};

export { APIError };

const apiService = {
  authAPI,
  authStorage,
  roomsAPI,
  messagesAPI,
  uploadAPI,
  resolveUploadUrl,
  getTenantInfoFromURL,
  APIError
};

export default apiService;